/**
 * Shared formatters for the admin console. Same primitives as the
 * inference + fleet consoles — kept colocated per surface so changes
 * to display conventions don't ripple across multiple repos.
 */

export const formatUsd = (n: number | undefined | null): string => {
  if (!n || n <= 0) return '$0.00';
  if (n < 0.01) return '<$0.01';
  if (n < 1) return `$${n.toFixed(4)}`;
  if (n < 1000) return `$${n.toFixed(2)}`;
  return `$${Math.round(n).toLocaleString()}`;
};

export const formatBytes = (b: number | undefined | null): string => {
  if (!b) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export const timeAgo = (date: string | Date | undefined | null): string => {
  if (!date) return 'Never';
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3_600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3_600)}h ago`;
  return `${Math.floor(diff / 86_400)}d ago`;
};

export const formatNumber = (n: number | undefined | null): string => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

/**
 * Full-precision integer formatter — for Customer Service / Escalations
 * / Audit Log tables where collapsing 1,234 to "1.2K" loses precision
 * the operator actually needs.
 */
export const fmtNum = (v: number | undefined | null): string =>
  v != null && Number.isFinite(v) ? Number(v).toLocaleString() : '—';

export const fmtDate = (d?: string | Date | null): string =>
  d ? new Date(d).toLocaleDateString() : '—';

export const fmtDateTime = (d?: string | Date | null): string =>
  d ? new Date(d).toLocaleString() : '—';

export const fmtDuration = (secs?: number | null): string => {
  if (!secs) return '0h';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export const PRIORITY_BADGE: Record<string, string> = {
  urgent: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  high: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  normal: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  low: 'bg-slate-500/20 text-slate-400 ring-1 ring-slate-500/30'
};

export const ESCALATION_STATUS_BADGE: Record<string, string> = {
  open: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  assigned: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  resolved: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  dismissed: 'bg-slate-500/20 text-slate-400 ring-1 ring-slate-500/30'
};
